import { PersistedStateName, saveToStorage } from "../helpers/persist";

type ChromecastNode = {
  player: cast.framework.RemotePlayer;
  playerController: cast.framework.RemotePlayerController;
};
type MediaNode = HTMLMediaElement | ChromecastNode;

const isChromecastNode = (media: MediaNode): media is ChromecastNode => {
  return "player" in media && "playerController" in media;
};

export const setMediaVolume = (
  media: MediaNode,
  volume: number,
  persist?: boolean,
  onError?: (currentValues: { isMuted: boolean; volume: number }) => void
): void => {
  try {
    const newVolume = Math.min(Math.max(volume, 0), 1);
    const isMuted = newVolume === 0;
    setVolumeForMediaNode(media, newVolume);
    setMutedForMediaNode(media, isMuted);
    if (persist) {
      saveToStorage(PersistedStateName.VOLUME, newVolume);
      saveToStorage(PersistedStateName.MUTED, isMuted);
    }
  } catch (err) {
    console.error("Failed to set volume", err);
    onError?.({
      isMuted: getMutedForMediaNode(media),
      volume: getVolumeForMediaNode(media)
    });
  }
};

export const setMediaMuted = (
  media: MediaNode,
  isMuted: boolean,
  persist?: boolean,
  onError?: (currentValues: { isMuted: boolean; volume: number }) => void
): void => {
  try {
    setMutedForMediaNode(media, isMuted);
    if (persist) {
      saveToStorage(PersistedStateName.MUTED, isMuted);
    }

    const currentVolume = isChromecastNode(media)
      ? media.player.volumeLevel
      : media.volume;
    if (!isMuted && currentVolume === 0) {
      const newVolume = 0.1;
      setVolumeForMediaNode(media, newVolume);
      if (persist) {
        saveToStorage(PersistedStateName.VOLUME, newVolume);
      }
    }
  } catch (err) {
    console.error("Failed to set muted", err);
    onError?.({
      isMuted: getMutedForMediaNode(media),
      volume: getVolumeForMediaNode(media)
    });
  }
};

const setVolumeForMediaNode = (media: MediaNode, volume: number): void => {
  if (isChromecastNode(media)) {
    if (media.player.volumeLevel !== volume) {
      media.player.volumeLevel = volume;
      media.playerController.setVolumeLevel();
    }
  } else {
    media.volume = volume;
  }
};

const setMutedForMediaNode = (media: MediaNode, isMuted: boolean): void => {
  if (isChromecastNode(media)) {
    if (media.player.isMuted !== isMuted) {
      media.playerController.muteOrUnmute();
    }
  } else {
    media.muted = isMuted;
  }
};

const getVolumeForMediaNode = (media: MediaNode): number => {
  if (isChromecastNode(media)) {
    return media.player.volumeLevel;
  }
  return media.volume;
};

const getMutedForMediaNode = (media: MediaNode): boolean => {
  if (isChromecastNode(media)) {
    return media.player.isMuted;
  }
  return media.muted;
};
